/**
 * scene-audio-manager.js
 * Hệ thống Quản lý Âm thanh & Thoại Chuẩn Hóa cho Voxel Anim Studio.
 *
 * NGUYÊN LÝ HOẠT ĐỘNG:
 * 1. Ưu tiên 1 (User MP3 Override): Nếu người dùng gán file MP3 (qua UI hoặc cấu hình),
 *    hệ thống ưu tiên tuyệt đối phát file MP3 này.
 * 2. Ưu tiên 2 (Default Engine - Edge-TTS On-The-Fly): Tự động stream giọng đọc AI tự nhiên
 *    chất lượng cao thời gian thực qua endpoint `/api/edge-tts`.
 * 3. Ưu tiên 3 (Graceful Fallback): Dự phòng Web Speech API khi ngoại tuyến hoàn toàn.
 */

export class SceneAudioManager {
  constructor(options = {}) {
    this.isEnabled = options.isEnabled ?? true;
    this.volume = options.volume ?? 1.0;

    // Quản lý audio HTML5 đang phát (tái sử dụng 1 element duy nhất để tránh bị browser chặn autoplay)
    this.audioElement = typeof Audio !== 'undefined' ? new Audio() : null;
    this.currentAudio = null;
    this.currentCueId = null;
    this.currentSourceType = null; // 'mp3_override' | 'edge_tts' | 'fallback_tts'

    // Bản đồ file MP3 ghi đè: cueId -> { url, blob, name, size }
    this.overrides = new Map();

    // Callbacks lắng nghe sự kiện
    this.listeners = {
      playStart: [],
      playEnd: [],
      overrideChange: []
    };

    // Cấu hình giọng Edge-TTS đặc thù theo từng nhân vật
    this.characterVoices = {
      TAI_XE_XANH_SM: { voice: 'vi-VN-NamMinhNeural', rate: '+5%', pitch: '+0Hz', name: 'Bác Tài Xanh SM' },
      TAI_XE_GRAB: { voice: 'vi-VN-NamMinhNeural', rate: '+8%', pitch: '+2Hz', name: 'Tài xế Grab' },
      BAC_BA_BA: { voice: 'vi-VN-NamMinhNeural', rate: '-5%', pitch: '-3Hz', name: 'Bác Ba Nam Bộ' },
      NGUOI_BAN_TRA_DA: { voice: 'vi-VN-NamMinhNeural', rate: '-5%', pitch: '-6Hz', name: 'Bác Bán Trà Đá' },
      NU_SINH_VIET: { voice: 'vi-VN-HoaiMyNeural', rate: '+0%', pitch: '+0Hz', name: 'Nữ Sinh Việt Nam' },
      CO_GAI_AO_DAI: { voice: 'vi-VN-HoaiMyNeural', rate: '-2%', pitch: '+1Hz', name: 'Cô Gái Áo Dài' },
      DEFAULT: { voice: 'vi-VN-NamMinhNeural', rate: '+0%', pitch: '+0Hz', name: 'Mặc định' }
    };

    // Khởi tạo bộ nhớ từ localStorage nếu có
    this.loadSavedOverrides();
  }

  /**
   * Đặt cấu hình giọng Edge-TTS cho nhân vật
   */
  setCharacterVoice(charKey, config) {
    this.characterVoices[charKey] = {
      ...(this.characterVoices[charKey] || this.characterVoices.DEFAULT),
      ...config
    };
  }

  getCharacterVoice(charKey) {
    return this.characterVoices[charKey] || this.characterVoices.DEFAULT;
  }

  /**
   * Đăng ký file MP3 ghi đè cho một câu thoại (User MP3 Override)
   * @param {string} cueId Mã định danh câu thoại (ví dụ: 's1_xanh', 'shot_1')
   * @param {Blob|string} audioBlobOrUrl Đối tượng Blob, File hoặc URL string
   * @param {string} fileName Tên file gốc người dùng tải lên
   */
  registerOverride(cueId, audioBlobOrUrl, fileName = 'custom_audio.mp3') {
    let url = '';
    let blob = null;

    if (audioBlobOrUrl instanceof Blob || audioBlobOrUrl instanceof File) {
      blob = audioBlobOrUrl;
      url = URL.createObjectURL(blob);
    } else if (typeof audioBlobOrUrl === 'string') {
      url = audioBlobOrUrl;
    }

    // Thu hồi URL cũ nếu có để tránh rò rỉ bộ nhớ
    const prev = this.overrides.get(cueId);
    if (prev && prev.url && prev.url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(prev.url);
      } catch {}
    }

    this.overrides.set(cueId, {
      url,
      blob,
      name: fileName,
      updatedAt: Date.now()
    });

    this.emit('overrideChange', { cueId, hasOverride: true, fileName });
  }

  /**
   * Xóa file MP3 ghi đè, hoàn tác về Edge-TTS mặc định
   */
  removeOverride(cueId) {
    const prev = this.overrides.get(cueId);
    if (prev) {
      if (prev.url && prev.url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev.url);
        } catch {}
      }
      this.overrides.delete(cueId);
      this.emit('overrideChange', { cueId, hasOverride: false });
    }
  }

  hasOverride(cueId) {
    return this.overrides.has(cueId);
  }

  getOverride(cueId) {
    return this.overrides.get(cueId) || null;
  }

  getAllOverrides() {
    const res = {};
    for (const [k, v] of this.overrides.entries()) {
      res[k] = { name: v.name, updatedAt: v.updatedAt };
    }
    return res;
  }

  /**
   * Xác định nguồn phát hiện tại cho một câu thoại: 'mp3_override' | 'edge_tts'
   */
  getSourceType(cueId) {
    return this.hasOverride(cueId) ? 'mp3_override' : 'edge_tts';
  }

  /**
   * Phát câu thoại theo đúng quy tắc ưu tiên:
  /**
   * Mở khóa AudioContext / HTML5 Audio Element trên trình duyệt (đặc biệt là iOS Safari và Chrome Mobile)
   * Cần gọi ngay trong callback tương tác đầu tiên của người dùng (Click Play / Chuyển cảnh)
   */
  unlock() {
    if (typeof Audio === 'undefined') return;
    if (!this.audioElement) {
      this.audioElement = new Audio();
    }
    if (this.audioElement.paused && (!this.audioElement.src || this.audioElement.src === '')) {
      this.audioElement.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==';
      const p = this.audioElement.play();
      if (p) {
        p.then(() => {
          this.audioElement.pause();
          this.audioElement.removeAttribute('src');
        }).catch(() => {});
      }
    }
  }

  /**
   * Phát một câu thoại (Cue) theo thứ tự ưu tiên:
   * Có file MP3 -> Ưu tiên phát MP3
   * Không có file MP3 -> Stream On-The-Fly từ Edge-TTS
   */
  playCue({
    cueId,
    text,
    speaker = '',
    characterId = '',
    customMp3Url = null,
    voice = null,
    rate = null,
    pitch = null
  }) {
    if (!this.isEnabled) return;
    if (!text || text.trim() === '') return;

    // Ngắt toàn bộ âm thanh và TTS đang phát trước đó
    this.stop();

    this.currentCueId = cueId;

    // 1. Kiểm tra ƯU TIÊN 1: File MP3 Ghi Đè (User Override)
    let audioSrc = null;
    let sourceType = 'edge_tts';

    if (customMp3Url) {
      audioSrc = customMp3Url;
      sourceType = 'mp3_override';
    } else if (this.hasOverride(cueId)) {
      audioSrc = this.overrides.get(cueId).url;
      sourceType = 'mp3_override';
    }

    // 2. Kiểm tra ƯU TIÊN 2: File MP3 Edge-TTS chuẩn của Cảnh Trà Đá (Pre-rendered Edge-TTS)
    const STREET_DRAMA_STATIC_CUES = new Set([
      's1_xanh',
      's1_grab',
      's2_xanh',
      's2_grab',
      's2_ba',
      's3_trada',
      's3_grab',
      's4_grab_attack',
      's4_grab_miss',
      's4_trada',
      's4_grab_dizzy',
      's5_grab_stumble',
      's5_trada',
      's5_xanh'
    ]);

    if (!audioSrc && cueId && STREET_DRAMA_STATIC_CUES.has(cueId)) {
      const origin =
        typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : '';
      audioSrc = `${origin}/audio/edge-tts/street-drama/${cueId}.mp3`;
      sourceType = 'edge_tts';
    }

    // 3. Nếu không có file tĩnh: Stream On-The-Fly qua /api/edge-tts (dành cho Storyboard / câu thoại mới)
    if (!audioSrc) {
      const vConfig = this.getCharacterVoice(characterId);
      const chosenVoice = voice || vConfig.voice || 'vi-VN-NamMinhNeural';
      const chosenRate = rate || vConfig.rate || '+0%';
      const chosenPitch = pitch || vConfig.pitch || '+0Hz';

      const origin =
        typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : '';
      audioSrc = `${origin}/api/edge-tts?text=${encodeURIComponent(text)}&voice=${encodeURIComponent(chosenVoice)}&rate=${encodeURIComponent(chosenRate)}&pitch=${encodeURIComponent(chosenPitch)}`;
      sourceType = 'edge_tts';
    }

    this.currentSourceType = sourceType;

    // 4. Khởi tạo phát qua Reusable HTML5 Audio Element (Đảm bảo không bị browser chặn autoplay ở các câu tiếp theo)
    if (!this.audioElement) {
      this.audioElement = new Audio();
    }
    const audio = this.audioElement;
    this.currentAudio = audio;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {}
    audio.volume = this.volume;
    audio.src = audioSrc;

    this.emit('playStart', {
      cueId,
      sourceType,
      text,
      speaker
    });

    audio.onended = () => {
      if (this.currentAudio === audio) {
        this.currentAudio = null;
        this.currentCueId = null;
        this.currentSourceType = null;
        this.emit('playEnd', { cueId });
      }
    };

    let fallbackTriggered = false;
    audio.onerror = (err) => {
      console.warn(`[SceneAudioManager] Không thể phát audio nguồn ${sourceType}:`, err);
      if (!fallbackTriggered && sourceType === 'edge_tts' && audioSrc && audioSrc.includes('/api/edge-tts')) {
        fallbackTriggered = true;
        this.fallbackWebSpeech(text);
      }
    };

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((e) => {
        if (e.name !== 'AbortError') {
          console.warn('[SceneAudioManager] Trình duyệt chưa cho phép autoplay:', e.message);
        }
      });
    }
  }

  /**
   * Dự phòng Web Speech API nếu môi trường không có kết nối tới endpoint Edge-TTS
   */
  fallbackWebSpeech(text) {
    if (!('speechSynthesis' in window)) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'vi-VN';
      utter.rate = 1.0;
      this.currentSourceType = 'fallback_tts';
      window.speechSynthesis.speak(utter);
    } catch {}
  }

  /**
   * Dừng sạch toàn bộ âm thanh (Audio + Web Speech API) tức thì
   * Đảm bảo tính năng Kill TTS khi Pause, Seek, Jump Scene hoặc chuyển Tab
   */
  stop() {
    if (this.audioElement) {
      try {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.audioElement.removeAttribute('src');
      } catch {}
    }
    this.currentAudio = null;

    if ('speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }

    this.currentCueId = null;
    this.currentSourceType = null;
  }

  setEnabled(enabled) {
    this.isEnabled = !!enabled;
    if (!this.isEnabled) {
      this.stop();
    }
  }

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.currentAudio) {
      this.currentAudio.volume = this.volume;
    }
  }

  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((cb) => {
        try {
          cb(data);
        } catch {}
      });
    }
  }

  loadSavedOverrides() {
    // Có thể mở rộng lưu cache metadata qua localStorage
  }

  dispose() {
    this.stop();
    for (const entry of this.overrides.values()) {
      if (entry.url && entry.url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(entry.url);
        } catch {}
      }
    }
    this.overrides.clear();
    this.listeners = { playStart: [], playEnd: [], overrideChange: [] };
  }
}
